import { useState } from 'react';
import { api } from '../lib/api.js';

type Perm = 'view' | 'comment' | 'edit';

interface Props {
  open: boolean;
  onClose: () => void;
  docId: string;
}

export const ShareDialog = ({ open, onClose, docId }: Props) => {
  const [perm, setPerm] = useState<Perm>('view');
  const [expiresHours, setExpiresHours] = useState<number>(0);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body: { docId: string; permission: Perm; expiresInSec?: number } = {
        docId,
        permission: perm,
      };
      if (expiresHours > 0) body.expiresInSec = expiresHours * 3600;
      const out = await api.createShare(body);
      setLink(out.url);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40"
      onClick={onClose}
      role="dialog"
    >
      <div
        className="w-[420px] max-w-[92vw] rounded-xl bg-white dark:bg-neutral-900 shadow-2xl ring-1 ring-black/10 p-5 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-neutral-800 dark:text-neutral-100">Share this canvas</h2>
          <button className="text-neutral-500 text-lg" onClick={onClose} aria-label="close">×</button>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Permission</label>
          <div className="flex gap-1.5">
            {(['view', 'comment', 'edit'] as Perm[]).map((p) => (
              <button
                key={p}
                onClick={() => setPerm(p)}
                className={`px-3 h-8 rounded-md text-xs font-medium capitalize ${
                  perm === p
                    ? 'bg-sky-500 text-white'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Expires in (hours, 0 = never)
          </label>
          <input
            type="number"
            min={0}
            value={expiresHours}
            onChange={(e) => setExpiresHours(Number(e.target.value))}
            className="h-8 px-2 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
          />
        </div>

        {err ? <div className="text-xs text-rose-500">{err}</div> : null}

        {link ? (
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link}
              className="flex-1 h-8 px-2 rounded-md border border-neutral-300 dark:border-neutral-700 text-xs font-mono bg-neutral-50 dark:bg-neutral-800"
            />
            <button
              onClick={copy}
              className="h-8 px-3 rounded-md bg-sky-500 text-white text-xs font-medium"
            >
              Copy
            </button>
          </div>
        ) : (
          <button
            disabled={busy}
            onClick={create}
            className="h-9 rounded-md bg-sky-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create share link'}
          </button>
        )}

        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-relaxed">
          Share link akan resolve via API worker (<code>/share/&lt;token&gt;</code>) dan redirect ke canvas dengan
          permission sesuai. Kalau collab aktif, recipient bergabung ke room realtime otomatis.
        </p>
      </div>
    </div>
  );
};
